
import { useState, useEffect } from 'react';

interface LoadingScreenProps {
  onLoadingComplete: () => void;
}

export default function LoadingScreen({ onLoadingComplete }: LoadingScreenProps) {
  const [progress, setProgress] = useState(0);
  const [currentText, setCurrentText] = useState('');
  const [showCursor, setShowCursor] = useState(true);
  const [loadingStep, setLoadingStep] = useState(0);

  const loadingSteps = [
    'Initializing system...',
    'Loading portfolio modules...',
    'Compiling C++ components...',
    'Establishing connections...',
    'Optimizing performance...',
    'Ready to execute!'
  ];

  useEffect(() => {
    // Cursor blinking effect
    const cursorInterval = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 500);

    // Progress and text animation
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev + 2;
        
        // Update loading step based on progress
        const stepIndex = Math.floor((newProgress / 100) * loadingSteps.length);
        if (stepIndex < loadingSteps.length && stepIndex !== loadingStep) {
          setLoadingStep(stepIndex);
          setCurrentText('');
        }
        
        return newProgress;
      });
    }, 60);

    // Typewriter effect for current step
    let textIndex = 0;
    const textInterval = setInterval(() => {
      if (loadingStep < loadingSteps.length && textIndex < loadingSteps[loadingStep].length) {
        setCurrentText(loadingSteps[loadingStep].slice(0, textIndex + 1));
        textIndex++;
      } else {
        textIndex = 0;
      }
    }, 50);

    // Complete loading after 3 seconds
    const completeTimer = setTimeout(() => {
      setProgress(100);
      setTimeout(() => {
        onLoadingComplete();
      }, 500);
    }, 3000);

    return () => {
      clearInterval(cursorInterval);
      clearInterval(progressInterval);
      clearInterval(textInterval);
      clearTimeout(completeTimer);
    };
  }, [loadingStep, onLoadingComplete]);

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col items-center justify-center overflow-hidden">
      {/* Matrix rain background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            className="absolute text-green-500/20 font-mono text-sm animate-matrix"
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 8}s`,
              animationDuration: `${6 + Math.random() * 4}s`
            }}
          >
            {String.fromCharCode(0x30A0 + Math.random() * 96)}
          </div>
        ))}
      </div>

      {/* Scanning line effect */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute w-full h-0.5 bg-gradient-to-r from-transparent via-green-500/50 to-transparent animate-scan" />
      </div>

      {/* Terminal window */}
      <div className="relative z-10 w-full max-w-2xl mx-4">
        {/* Terminal header */}
        <div className="bg-gray-900 border border-green-500/30 rounded-t-lg p-3">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
            <div className="w-3 h-3 bg-yellow-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse animate-glow" style={{ animationDelay: '0.4s' }}></div>
            <span className="text-green-400 text-sm ml-4 animate-pulse">~/portfolio/boot</span>
          </div>
        </div>

        {/* Terminal content */}
        <div className="bg-black border-x border-b border-green-500/30 rounded-b-lg p-8">
          <div className="space-y-6">
            {/* ASCII Art Logo */}
            <div className="text-green-400 font-mono text-center animate-slide-in-up">
              <pre className="text-xs md:text-sm leading-tight">
{`
 ██████╗ ██████╗ ██████╗ ████████╗███████╗ ██████╗ ██╗     ██╗ ██████╗ 
██╔══██╗██╔═══██╗██╔══██╗╚══██╔══╝██╔════╝██╔═══██╗██║     ██║██╔═══██╗
██████╔╝██║   ██║██████╔╝   ██║   █████╗  ██║   ██║██║     ██║██║   ██║
██╔═══╝ ██║   ██║██╔══██╗   ██║   ██╔══╝  ██║   ██║██║     ██║██║   ██║
██║     ╚██████╔╝██║  ██║   ██║   ██║     ╚██████╔╝███████╗██║╚██████╔╝
╚═╝      ╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚═╝      ╚═════╝ ╚══════╝╚═╝ ╚═════╝ 
`}
              </pre>
            </div>

            {/* Loading text */}
            <div className="text-left space-y-4">
              <div className="text-green-400 animate-slide-in-left">
                <span className="text-green-500 animate-pulse">$</span> ./initialize_portfolio.sh
              </div>
              
              <div className="text-gray-300 pl-4 animate-slide-in-right" style={{ animationDelay: '0.3s' }}>
                <div className="mb-4">
                  {currentText}
                  <span className={`${showCursor ? 'opacity-100' : 'opacity-0'} transition-opacity text-green-500`}>|</span>
                </div>
              </div>

              {/* Progress bar */}
              <div className="pl-4 animate-slide-in-up" style={{ animationDelay: '0.6s' }}>
                <div className="flex items-center space-x-4">
                  <span className="text-green-400 text-sm">Progress:</span>
                  <div className="flex-1 bg-gray-800 rounded-full h-2 overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-green-600 to-green-400 transition-all duration-300 ease-out animate-glow"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                  <span className="text-green-400 text-sm font-mono">{progress.toFixed(0)}%</span>
                </div>
              </div>

              {/* System info */}
              <div className="text-gray-500 text-xs pl-4 space-y-1 animate-slide-in-left" style={{ animationDelay: '0.9s' }}>
                <div>System: Linux x86_64</div>
                <div>Compiler: GCC 11.4.0</div>
                <div>Runtime: Node.js v18.17.0</div>
                <div>Framework: React 19.0.0</div>
              </div>

              {/* Loading dots animation */}
              <div className="flex items-center space-x-2 pl-4 animate-slide-in-right" style={{ animationDelay: '1.2s' }}>
                <span className="text-green-400">Loading</span>
                <div className="flex space-x-1">
                  <div className="w-1 h-1 bg-green-500 rounded-full animate-pulse"></div>
                  <div className="w-1 h-1 bg-green-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                  <div className="w-1 h-1 bg-green-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-green-500/30 rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 3}s`
            }}
          />
        ))}
      </div>

      {/* Grid overlay */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="w-full h-full" style={{
          backgroundImage: `
            linear-gradient(rgba(34,197,94,0.3) 1px, transparent 1px),
            linear-gradient(90deg, rgba(34,197,94,0.3) 1px, transparent 1px)
          `,
          backgroundSize: '20px 20px'
        }} />
      </div>
    </div>
  );
}
